import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CSSModules from 'react-css-modules';
import isEqual from 'lodash/isEqual';
import find from 'lodash/find';
import VisibilitySensor from 'react-visibility-sensor';
import { injectConfig, configShape } from '@client/hoc/config';
import { getClientDeviceWidth } from '@client/redux/modules/clientDevice/selectors';
import {
    saveImpressionData as saveImpressionDataFromRedux,
    clearImpressionData as clearImpressionDataFromRedux,
} from '@client/redux/modules/tracking/actions';
import { getImpressionData } from '@client/redux/modules/tracking/selectors';
import {
    TRACK_RECO_COLLABORATIVE,
    TRACK_RECO_TOPSELLER,
} from '@client/config/constants';
import { visibleOnce, track, makeLoopFunction } from '@client/helpers';
import {
    getRecoImpressionData,
    getTrackingDataProductClick,
} from '@client/helpers/tracking';
import { getImageUrl } from '@client/components/shared/Helpers';
import Carousel from '@client/components/shared/carousel/Carousel';
import Recommendation from '../Recommendation/index.js';
import styles from './styles.scss';

export class RecommendationsCarousel extends Component {
    constructor(props) {
        super(props);
        const articleVisibilityFunc = {};
        props.recommendations.forEach((reco, index) => {
            articleVisibilityFunc[reco.sku] = visibleOnce(
                {
                    ...reco,
                    position: index + 1,
                    source:
                        props.source ||
                        (reco.reco_tracking_string &&
                            TRACK_RECO_COLLABORATIVE) ||
                        TRACK_RECO_TOPSELLER,
                },
                props.saveImpressionData,
            );
        });
        this.state = { articleVisibilityFunc, blockRedirect: false };
    }

    static propTypes = {
        recommendations: PropTypes.array.isRequired,
        itemWidth: PropTypes.number,
        source: PropTypes.number,
        width: PropTypes.number.isRequired,
        withoutHeader: PropTypes.bool,
        saveImpressionData: PropTypes.func.isRequired,
        clearImpressionData: PropTypes.func.isRequired,
        impressionData: PropTypes.array.isRequired,
        trackByContainer: PropTypes.bool,
        deviceWidth: PropTypes.number.isRequired,
        config: configShape,
        newCardStyle: PropTypes.bool,
        onRecoClick: PropTypes.func,
        onSwipe: PropTypes.func
    };

    render() {
        if (!this.props.recommendations.length) {
            return null;
        }

        const { config, newCardStyle } = this.props;

        return (
            <div styleName="wrapper">
                <div styleName="carousel-wrapper" ref={this.carouselRef}>
                    <div styleName="carousel">
                        <Carousel
                            beforeChange={this.beforeChange}
                            afterChange={this.afterChange}
                            slidesToShow={this.getSlidesToShow()}
                        >
                            {this.props.recommendations.map((reco, index) => (
                                <div
                                    key={reco.sku}
                                    styleName="carousel-slide"
                                >
                                    <VisibilitySensor
                                        onChange={
                                            this.state.articleVisibilityFunc[
                                                reco.sku
                                            ]
                                        }
                                        delayedCall={true}
                                        intervalDelay={800}
                                        containment={
                                            this.props.trackByContainer &&
                                            this.carouselRef &&
                                            this.carouselRef.current
                                        }
                                    >
                                        <Recommendation
                                            imageUrl={getImageUrl(
                                                config,
                                                'zlcatalog',
                                                reco.imageUrl,
                                            )}
                                            campaignIdentifier={
                                                reco.campaignIdentifier
                                            }
                                            brand={reco.brand}
                                            sku={reco.sku}
                                            price={reco.price}
                                            specialPrice={reco.specialPrice}
                                            similarPrices={reco.similarPrices}
                                            stockStatus={reco.stockStatus}
                                            onClick={this.onRecoClick}
                                            source={this.props.source}
                                            position={index}
                                            newCardStyle={newCardStyle}
                                        />
                                    </VisibilitySensor>
                                </div>
                            ))}
                        </Carousel>
                    </div>
                </div>
            </div>
        );
    }

    getSlidesToShow = () => {
        const maxPossible = Math.floor(this.props.width / this.props.itemWidth);

        return Math.min(maxPossible, this.props.recommendations.length);
    };

    componentDidMount() {
        this.startImpressionTracking();
    }

    shouldComponentUpdate(nextProps) {
        return (
            !isEqual(this.props.recommendations, nextProps.recommendations) ||
            !isEqual(this.props.width, nextProps.width)
        );
    }

    componentWillUnmount() {
        this.stopImpressionTracking();
    }

    onRecoClick = (e, sku, recoPosition) => {
        const reco = find(this.props.recommendations, { sku });

        // clear the uncalled visibleOnce callback to prevent unwanted impression
        this.setState({ articleVisibilityFunc: {} });

        if (this.state.blockRedirect) {
            e.preventDefault();
        }

        this.trackRecoClick(reco, recoPosition);
        if (this.props.onRecoClick) {
            this.props.onRecoClick();
        }
    };

    // TODO: why do we have this here?
    trackRecoClick = (reco, recoPosition) => {
        const { source } = this.props;
        const trackingData = getTrackingDataProductClick(
            reco,
            recoPosition,
            source,
        );
        track('productClick', trackingData);
    };

    startImpressionTracking = () => {
        this.poolTracking = makeLoopFunction(this.impressionTracking, 5000);
        this.poolTracking.start();
    };

    impressionTracking = () => {
        const { config } = this.props;

        if (this.props.impressionData.length > 0) {
            const payload = getRecoImpressionData(
                this.props.impressionData,
                undefined,
                config.appdomain,
            );
            track('eec_impressions', payload);
            this.props.clearImpressionData();
        }
    };

    stopImpressionTracking = () => {
        this.poolTracking.stop();
        // tracking here before leaving
        this.impressionTracking();
        // clear the instance on leaving becasue we need to track whenever users come back here
        this.setState({ articleVisibilityFunc: {} });
    };

    beforeChange = () => {
        this.setState({ blockRedirect: true });
    };

    afterChange = () => {
        this.setState({ blockRedirect: false });
        if (this.props.onSwipe) {
            this.props.onSwipe();
        }
    };

    carouselRef = React.createRef();
}

const mapStateToProps = state => ({
    impressionData: getImpressionData(state),
    deviceWidth: getClientDeviceWidth(state),
});

const mapDispatchToProps = {
    saveImpressionData: saveImpressionDataFromRedux,
    clearImpressionData: clearImpressionDataFromRedux,
};

export const RecommendationsCarouselWithStyles = CSSModules(
    RecommendationsCarousel,
    styles,
    { allowMultiple: true },
);

export default injectConfig(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    )(RecommendationsCarouselWithStyles),
);
